import React, { useEffect, useState } from "react";
import SellerReviewService from "../../../services/sellerReview.service";
import jwtDecode from "jwt-decode";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "../../../styles/seller-reviews.css";

const SellerReviewList = ({ sellerId }) => {
    const [reviews, setReviews] = useState([]);
    const [avgData, setAvgData] = useState({ averageRating: 0, totalReviews: 0 });
    const [loading, setLoading] = useState(true);

    // Form state
    const [rating, setRating] = useState(0);
    const [reviewText, setReviewText] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [editingReviewId, setEditingReviewId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const token = localStorage.getItem("user");
    const decoded = token ? jwtDecode(token) : null;
    const currentUserId = decoded?.sub;
    const isSeller = currentUserId && parseInt(currentUserId) === parseInt(sellerId);

    const fetchReviews = () => {
        setLoading(true);
        SellerReviewService.getSellerReviews(sellerId)
            .then((res) => {
                console.log("DEBUG: fetchReviews response:", res);
                if (res.data.status) {
                    setReviews(res.data.data);
                    const myReview = res.data.data.find(r => r.buyer.id.toString() === currentUserId?.toString());
                    if (myReview) {
                        setRating(myReview.rating);
                        setReviewText(myReview.reviewText);
                        setIsEditing(true);
                        setEditingReviewId(myReview.id);
                    }
                }
            })
            .catch((err) => {
                console.error("DEBUG: Error fetching reviews:", err.response || err);
            })
            .finally(() => setLoading(false));

        SellerReviewService.getSellerAverageRating(sellerId)
            .then((res) => {
                if (res.data.status) {
                    setAvgData(res.data.data);
                }
            })
            .catch((err) => console.error("Error fetching average rating:", err));
    };

    useEffect(() => {
        if (sellerId) {
            fetchReviews();
        }
    }, [sellerId, currentUserId]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (rating === 0) {
            toast.error("Please select a star rating");
            return;
        }
        if (!currentUserId) {
            toast.error("Please login to submit a review");
            return;
        }

        setSubmitting(true);
        const reviewData = {
            sellerId: parseInt(sellerId),
            buyerId: parseInt(currentUserId),
            rating: rating,
            reviewText: reviewText,
            id: editingReviewId
        };

        console.log("DEBUG: Submitting review data:", reviewData);
        SellerReviewService.addReview(reviewData)
            .then((res) => {
                console.log("DEBUG: addReview successful call. Status:", res.status, "Body:", res.data);
                if (res.data.status) {
                    toast.success(isEditing ? "Review updated!" : "Review submitted!");
                    fetchReviews();
                } else {
                    console.warn("DEBUG: API returned success status but business failure:", res.data);
                    toast.error(res.data.message || "Failed to submit review");
                }
            })
            .catch((err) => {
                console.error("DEBUG: Submission failed spectacularly!");
                console.error("DEBUG: Error object:", err);
                let fullError = "Unknown error";
                if (err.response) {
                    console.error("DEBUG: Response Status:", err.response.status);
                    console.error("DEBUG: Response Data:", err.response.data);
                    fullError = JSON.stringify(err.response.data);
                } else {
                    fullError = err.message;
                }
                toast.error(`Error ${err.response?.status || ''}: ${fullError}`);
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <div className="seller-reviews-container">
            <div className="reviews-header mb-4 p-4 card border-0 shadow-sm" style={{ background: "#fff", borderRadius: "15px" }}>
                <h4 className="fw-bold mb-3">Seller Reviews</h4>
                <div className="avg-rating-display d-flex align-items-center">
                    {avgData.totalReviews > 0 ? (
                        <>
                            <span className="stars fs-4">
                                {"★".repeat(Math.round(avgData.averageRating))}
                                <span style={{ color: "#ddd" }}>{"★".repeat(5 - Math.round(avgData.averageRating))}</span>
                            </span>
                            <span className="rating-value ms-3 fw-bold fs-5">{avgData.averageRating} / 5.0</span>
                            <span className="text-muted ms-3">({avgData.totalReviews} total reviews)</span>
                        </>
                    ) : (
                        <span className="text-muted">No ratings yet</span>
                    )}
                </div>
            </div>

            {/* Review Form (Only for buyers, not the seller themselves) */}
            {currentUserId && !isSeller && (
                <div className="review-form-card mb-5 p-4 card border-0 shadow-sm" style={{ background: "#fff", borderRadius: "15px" }}>
                    <h5 className="fw-bold mb-4">{isEditing ? "Update Your Review" : "Rate this Seller"}</h5>
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <label className="form-label text-muted small fw-bold">YOUR RATING</label>
                            <div className="star-rating">
                                {[5, 4, 3, 2, 1].map((star) => (
                                    <React.Fragment key={star}>
                                        <input
                                            type="radio"
                                            id={`star${star}`}
                                            name="rating"
                                            value={star}
                                            checked={rating === star}
                                            onChange={() => setRating(star)}
                                        />
                                        <label htmlFor={`star${star}`} style={{ fontSize: "35px" }}>★</label>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="form-label text-muted small fw-bold text-uppercase">Your Feedback</label>
                            <textarea
                                className="form-control border-0 bg-light p-3"
                                placeholder="Share your experience (communication, service, quality)..."
                                rows="4"
                                style={{ borderRadius: "12px", resize: "none" }}
                                value={reviewText}
                                onChange={(e) => setReviewText(e.target.value)}
                            ></textarea>
                        </div>
                        <div className="d-grid">
                            <button
                                type="submit"
                                className="btn btn-primary btn-lg fw-bold border-0 shadow-sm"
                                style={{ borderRadius: "12px", padding: "12px", background: "#3498db" }}
                                disabled={submitting}
                            >
                                {submitting ? "Submitting..." : (isEditing ? "Update Review" : "Submit Review")}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Reviews List */}
            <div className="reviews-list">
                {loading ? (
                    <p>Loading reviews...</p>
                ) : (reviews.length === 0 && avgData.totalReviews === 0) ? (
                    <p className="text-muted">No reviews yet for this seller.</p>
                ) : reviews.length === 0 ? (
                    <p className="text-muted">Reviews are being processed...</p>
                ) : (
                    reviews.map((review) => (
                        <div key={review.id} className="review-card">
                            <div className="review-header">
                                <span className="review-buyer">{review.buyer.name} {review.buyer.id.toString() === currentUserId?.toString() && "(You)"}</span>
                                <span className="review-date">{dayjs(review.createdAt).format("MMM DD, YYYY")}</span>
                            </div>
                            <div className="review-stars">
                                {"★".repeat(review.rating)}
                                <span style={{ color: "#ddd" }}>{"★".repeat(5 - review.rating)}</span>
                            </div>
                            <p className="review-text mt-2">{review.reviewText}</p>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default SellerReviewList;
